import { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Card, CardBody } from 'reactstrap'
import agentService from '../../services/agentService'
import toastService from '../../services/toastService'
import { confirm } from '../Common/Modals/ConfirmService'
import Loader from '../Loader/Loader'
import styles from './CommissionStatements.module.scss'

const STATUS_LABELS = { pending: 'Pending approval', approved: 'Approved — to pay', paid: 'Paid' }

const money = (value) => `$${Number(value || 0).toFixed(2)}`

const previousMonth = () => dayjs().subtract(1, 'month').format('YYYY-MM')

/**
 * Monthly agent commission statements.
 * master: all agents, generate for a month, approve (emails the agent) and mark as paid.
 * otherwise: the logged-in agent's approved and paid statements.
 */
const CommissionStatements = ({ master = false }) => {
  const [statements, setStatements] = useState([])
  const [period, setPeriod] = useState(master ? previousMonth() : '')
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(null)

  const load = () => {
    setLoading(true)
    const request = master
      ? agentService.getCommissionStatements({ period })
      : agentService.getAgentCommissionStatements()
    request
      .then((response) => setStatements(response?.statements || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [period])

  const generate = () => {
    agentService
      .generateCommissionStatements({ period })
      .then((response) => {
        toastService.show(
          'success',
          `${response?.statements?.length || 0} statement(s) for ${period}`,
        )
        load()
      })
      .catch(() => {})
  }

  const approve = (statement) => {
    confirm(
      `Approve ${money(statement.commission)} commission for ${statement.agentUsername} (${
        statement.period
      })? The statement will be emailed to the agent.`,
      { title: 'Approve commission statement' },
    ).then(
      () =>
        agentService
          .approveCommissionStatement(statement._id)
          .then(() => {
            toastService.show(
              'success',
              `Statement approved and sent to ${statement.agentUsername}`,
            )
            load()
          })
          .catch(() => {}),
      () => {},
    )
  }

  const markPaid = (statement) => {
    confirm(
      `Mark ${money(statement.commission)} for ${statement.agentUsername} (${
        statement.period
      }) as paid?`,
      { title: 'Commission paid' },
    ).then(
      () =>
        agentService
          .markCommissionStatementPaid(statement._id)
          .then(() => {
            toastService.show('success', 'Statement marked as paid')
            load()
          })
          .catch(() => {}),
      () => {},
    )
  }

  const total = statements.reduce((sum, statement) => sum + (statement.commission || 0), 0)

  return (
    <Card className='mb-4 card-custom'>
      <div className={styles.header}>
        <h6 className='m-2'>Commission statements</h6>
        {master ? (
          <div className={styles.controls}>
            <label htmlFor='statementMonth'>Month:</label>
            <input
              id='statementMonth'
              className='form-control bordered'
              type='month'
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              onClick={(e) => {
                try {
                  e.currentTarget.showPicker?.()
                } catch (err) {
                  // not supported here; the field can still be typed in
                }
              }}
              title='Choose the month of the statements'
            />
            <button className='btn btn-lightblue' onClick={generate} disabled={!period}>
              Generate
            </button>
          </div>
        ) : null}
      </div>
      <CardBody className='p-0 pb-3 table-responsive'>
        {loading ? (
          <Loader />
        ) : statements.length === 0 ? (
          <p className='m-2 small'>
            {master
              ? 'No statements for this month. Statements are generated on the 1st of each month for the previous month.'
              : 'No commission statements yet.'}
          </p>
        ) : (
          <table className='table mb-0'>
            <thead className='lightblue-bg'>
              <tr>
                <th className='border-0'>Month</th>
                {master ? <th className='border-0'>Agent</th> : null}
                <th className='border-0 text-end'>Customer payments</th>
                <th className='border-0 text-end'>Rate</th>
                <th className='border-0 text-end'>Commission</th>
                <th className='border-0'>Status</th>
                {master ? <th className='border-0 text-center'>Action</th> : null}
              </tr>
            </thead>
            <tbody>
              {statements.map((statement) => (
                <Fragment key={statement._id}>
                  <tr
                    className={styles.row}
                    onClick={() => setExpanded(expanded === statement._id ? null : statement._id)}
                  >
                    <td>{dayjs(statement.period + '-01').format('MMM YYYY')}</td>
                    {master ? <td>{statement.agentUsername}</td> : null}
                    <td className='text-end'>{money(statement.sales)}</td>
                    <td className='text-end'>{statement.commissionPercent}%</td>
                    <td className='text-end'>
                      <strong>{money(statement.commission)}</strong>
                    </td>
                    <td>{STATUS_LABELS[statement.status] || statement.status}</td>
                    {master ? (
                      <td className='text-center' onClick={(e) => e.stopPropagation()}>
                        {statement.status === 'pending' ? (
                          <button
                            className='btn btn-lightblue btn-sm'
                            onClick={() => approve(statement)}
                          >
                            Approve
                          </button>
                        ) : null}
                        {statement.status === 'approved' ? (
                          <button
                            className='btn btn-lightblue btn-sm'
                            onClick={() => markPaid(statement)}
                          >
                            Mark as paid
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                  {expanded === statement._id ? (
                    <tr>
                      <td colSpan={master ? 7 : 5}>
                        <table className={`table table-sm mb-0 ${styles.lines}`}>
                          <tbody>
                            {statement.lines.map((line) => (
                              <tr key={line.transactionId}>
                                <td>{dayjs(line.date).format('MMM D, YYYY')}</td>
                                <td>{line.organizationName || line.username}</td>
                                <td>{line.type}</td>
                                <td className='text-end'>{money(line.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
            {master ? (
              <tfoot>
                <tr>
                  <td colSpan={4} className='text-end'>
                    Total commission
                  </td>
                  <td className='text-end'>
                    <strong>{money(total)}</strong>
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            ) : null}
          </table>
        )}
      </CardBody>
    </Card>
  )
}

CommissionStatements.propTypes = {
  master: PropTypes.bool,
}

export default CommissionStatements
